import type { BadgeType } from "./components/MovementServiceSummary";

// Backend movement service statuses
export type MovementServiceBackendStatus =
  | "AWAITING_ARRIVAL_TERMINAL_APPROVAL"
  | "AWAITING_DEPARTURE_TERMINAL_APPROVAL"
  | "AWAITING_MARINE_DEPARTMENT_APPROVAL"
  | "REJECTED_BY_DEPARTURE_TERMINAL"
  | "REJECTED_BY_ARRIVAL_TERMINAL"
  | "REJECTED_BY_MARINE_DEPARTMENT"
  | "APPROVED"
  | "CLOSED"
  | "CANCELLED_BY_SHIPPING_AGENT"
  | "CANCELLED_BY_MARINE_DEPARTMENT"
  | "MOVEMENT_STARTED"
  | "NULL";

// Maps backend status to translation key
export const mapMovementServiceStatus = (
  status: string,
): { translationKey: string; badgeType: BadgeType } => {
  const statusMap: Record<
    MovementServiceBackendStatus,
    { translationKey: string; badgeType: BadgeType }
  > = {
    AWAITING_ARRIVAL_TERMINAL_APPROVAL: {
      translationKey: "vessel-call.movement-services.status.awaiting-arrival-terminal-approval",
      badgeType: "warning",
    },
    AWAITING_DEPARTURE_TERMINAL_APPROVAL: {
      translationKey: "vessel-call.movement-services.status.awaiting-departure-terminal-approval",
      badgeType: "warning",
    },
    AWAITING_MARINE_DEPARTMENT_APPROVAL: {
      translationKey: "vessel-call.movement-services.status.awaiting-marine-department-approval",
      badgeType: "warning",
    },
    REJECTED_BY_DEPARTURE_TERMINAL: {
      translationKey: "vessel-call.movement-services.status.rejected-by-departure-terminal",
      badgeType: "error",
    },
    REJECTED_BY_ARRIVAL_TERMINAL: {
      translationKey: "vessel-call.movement-services.status.rejected-by-arrival-terminal",
      badgeType: "error",
    },
    REJECTED_BY_MARINE_DEPARTMENT: {
      translationKey: "vessel-call.movement-services.status.rejected-by-marine-department",
      badgeType: "error",
    },
    APPROVED: {
      translationKey: "vessel-call.movement-services.status.approved",
      badgeType: "success",
    },
    CLOSED: {
      translationKey: "vessel-call.movement-services.status.closed",
      badgeType: "success",
    },
    CANCELLED_BY_SHIPPING_AGENT: {
      translationKey: "vessel-call.movement-services.status.cancelled-by-shipping-agent",
      badgeType: "error",
    },
    CANCELLED_BY_MARINE_DEPARTMENT: {
      translationKey: "vessel-call.movement-services.status.cancelled-by-marine-department",
      badgeType: "error",
    },
    MOVEMENT_STARTED: {
      translationKey: "vessel-call.movement-services.status.movement-started",
      badgeType: "success",
    },
    NULL: {
      translationKey: "vessel-call.movement-services.status.null",
      badgeType: "warning",
    },
  };

  const mapped = statusMap[status as MovementServiceBackendStatus];
  if (mapped) {
    return mapped;
  }

  // Fallback for unknown statuses
  return {
    translationKey: status,
    badgeType: "warning",
  };
};

// Updated STATUS_TO_BADGE mapping including backend statuses
export const MOVEMENT_SERVICE_STATUS_TO_BADGE: Record<string, BadgeType> = {
  // Backend statuses
  "AWAITING_ARRIVAL_TERMINAL_APPROVAL": "warning",
  "AWAITING_DEPARTURE_TERMINAL_APPROVAL": "warning",
  "AWAITING_MARINE_DEPARTMENT_APPROVAL": "warning",
  "APPROVED": "success",
  "REJECTED_BY_DEPARTURE_TERMINAL": "error",
  "REJECTED_BY_ARRIVAL_TERMINAL": "error",
  "REJECTED_BY_TERMINAL": "error",
  "REJECTED_BY_MARINE_DEPARTMENT": "error",
  "CLOSED": "success",
  "CANCELLED_BY_SHIPPING_AGENT": "error",
  "CANCELLED_BY_MARINE_DEPARTMENT": "error",
  "CANCELLED": "error",
  // TODO -Remove if no longer needed
  // Old statuses
  "Approved": "success",
  "Pending Approval": "warning",
  "Completed": "success",
  "Closed": "success",
  "Cancelled": "error",
  "Rejected": "error",
};
